import React from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Wrench, Users, GraduationCap, Headphones, Clock } from 'lucide-react';
import { SalesQualificationStepProps } from '@/types/onboarding/salesQualification';
import { createArrayUpdateHelper } from '../utils';

export const ImplementationReadiness: React.FC<SalesQualificationStepProps> = ({
  data,
  onUpdate,
}) => {
  const updateFormData = (field: string, value: unknown) => {
    onUpdate({ ...data, [field]: value });
  };

  const handleArrayUpdate = createArrayUpdateHelper(data, updateFormData);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Implementation Readiness</h2>
        <p className="text-gray-600">
          Help us understand your implementation capacity and requirements for enterprise deployment
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Implementation Capacity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="w-5 h-5 text-orange-600" />
              Implementation Capacity
            </CardTitle>
            <CardDescription>
              How much internal capacity do you have for implementation?
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select
              value={data.implementationCapacity || ''}
              onValueChange={(value) => updateFormData('implementationCapacity', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select your capacity level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full-support">
                  Need full implementation support - minimal internal capacity
                </SelectItem>
                <SelectItem value="some-capacity">
                  Some internal capacity available - can assist with implementation
                </SelectItem>
                <SelectItem value="good-capacity">
                  Good internal technical capacity - can lead with guidance
                </SelectItem>
                <SelectItem value="self-sufficient">
                  Highly technical team - can self-implement with minimal support
                </SelectItem>
                <SelectItem value="dedicated-team">
                  Dedicated internal implementation team available
                </SelectItem>
              </SelectContent>
            </Select>

            <div className="mt-4">
              <Label htmlFor="internalResources">Describe your internal technical resources</Label>
              <Textarea
                id="internalResources"
                value={data.internalResources || ''}
                onChange={(e) => updateFormData('internalResources', e.target.value)}
                placeholder="e.g., IT manager with cloud experience, property manager familiar with software implementations, board member with technical background..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Change Management */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-green-600" />
              Change Management Approach
            </CardTitle>
            <CardDescription>How do you typically handle organizational change?</CardDescription>
          </CardHeader>
          <CardContent>
            <Select
              value={data.changeManagementApproach || ''}
              onValueChange={(value) => updateFormData('changeManagementApproach', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select your preferred approach" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gradual">
                  Gradual, phased approach with incremental rollouts
                </SelectItem>
                <SelectItem value="quick">
                  Quick implementation preferred - get up and running fast
                </SelectItem>
                <SelectItem value="pilot">
                  Pilot program with small group first, then full rollout
                </SelectItem>
                <SelectItem value="comprehensive">
                  Comprehensive planning and training before launch
                </SelectItem>
                <SelectItem value="parallel">
                  Run parallel systems during transition period
                </SelectItem>
                <SelectItem value="big-bang">
                  Full cutover approach - switch everything at once
                </SelectItem>
              </SelectContent>
            </Select>

            <div className="mt-4">
              <Label htmlFor="changeExperience">
                Previous experience with similar implementations
              </Label>
              <Textarea
                id="changeExperience"
                value={data.changeExperience || ''}
                onChange={(e) => updateFormData('changeExperience', e.target.value)}
                placeholder="e.g., Recently switched property management software, implemented new financial system, upgraded website platform..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Training Needs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-blue-600" />
            Training Needs
          </CardTitle>
          <CardDescription>
            What training will be needed for successful adoption? (Check all that apply)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              'Board member training (governance features)',
              'Administrative staff training (daily operations)',
              'Property manager training (comprehensive platform)',
              'Financial staff training (treasury and accounting)',
              'Resident orientation (community portal features)',
              'Vendor training (vendor portal and processes)',
              'Security and access management training',
              'Custom workflow and automation training',
              'Reporting and analytics training',
              'Integration management training',
              'Ongoing refresher training programs',
              'Train-the-trainer programs for internal champions',
            ].map((training) => (
              <div key={training} className="flex items-start space-x-2">
                <Checkbox
                  id={`training-${training}`}
                  checked={data.trainingNeeds?.includes(training) || false}
                  onCheckedChange={(checked) =>
                    handleArrayUpdate('trainingNeeds', training, !!checked)
                  }
                />
                <Label htmlFor={`training-${training}`} className="text-sm leading-relaxed">
                  {training}
                </Label>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <Label className="text-sm font-medium">Preferred Training Format</Label>
            <RadioGroup
              value={data.preferredTrainingFormat || ''}
              onValueChange={(value) => updateFormData('preferredTrainingFormat', value)}
              className="mt-2"
            >
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="remote" id="remote-training" />
                  <Label htmlFor="remote-training" className="text-sm">
                    Remote/virtual training sessions
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="onsite" id="onsite-training" />
                  <Label htmlFor="onsite-training" className="text-sm">
                    On-site training at our location
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="hybrid" id="hybrid-training" />
                  <Label htmlFor="hybrid-training" className="text-sm">
                    Hybrid approach (mix of remote and on-site)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="self-paced" id="self-paced" />
                  <Label htmlFor="self-paced" className="text-sm">
                    Self-paced online learning materials
                  </Label>
                </div>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

      {/* Support Requirements */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Headphones className="w-5 h-5 text-purple-600" />
            Support Requirements
          </CardTitle>
          <CardDescription>
            What ongoing support will you need post-implementation? (Check all that apply)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              '24/7 technical support for critical issues',
              'Business hours support (9am-5pm)',
              'Dedicated account manager',
              'Priority phone support line',
              'Email support with SLA guarantees',
              'Live chat support integration',
              'Remote screen sharing for troubleshooting',
              'Scheduled check-in calls and reviews',
              'Proactive monitoring and alerts',
              'Custom development and enhancement support',
              'Integration support for new systems',
              'Regular training and best practices sessions',
              'Community user groups and forums',
              'Comprehensive documentation and knowledge base',
            ].map((support) => (
              <div key={support} className="flex items-start space-x-2">
                <Checkbox
                  id={`support-${support}`}
                  checked={data.supportRequirements?.includes(support) || false}
                  onCheckedChange={(checked) =>
                    handleArrayUpdate('supportRequirements', support, !!checked)
                  }
                />
                <Label htmlFor={`support-${support}`} className="text-sm leading-relaxed">
                  {support}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Implementation Timeline & Constraints */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-red-600" />
              Implementation Constraints
            </CardTitle>
            <CardDescription>What constraints affect your implementation timeline?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                'Board meeting schedule (need approvals)',
                'Budget year timing (fiscal calendar)',
                'Seasonal restrictions (busy periods to avoid)',
                'Staff availability and vacation schedules',
                'Other major initiatives or projects',
                'Regulatory compliance deadlines',
                'Annual meeting or election timing',
                'Current contract obligations',
                'Holiday and event calendar conflicts',
                'No significant constraints',
              ].map((constraint) => (
                <div key={constraint} className="flex items-start space-x-2">
                  <Checkbox
                    id={`constraint-${constraint}`}
                    checked={data.implementationConstraints?.includes(constraint) || false}
                    onCheckedChange={(checked) =>
                      handleArrayUpdate('implementationConstraints', constraint, !!checked)
                    }
                  />
                  <Label htmlFor={`constraint-${constraint}`} className="text-sm leading-relaxed">
                    {constraint}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Success Metrics</CardTitle>
            <CardDescription>How will you measure implementation success?</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                'User adoption rates (percentage actively using)',
                'Time savings in administrative tasks',
                'Reduction in manual processes',
                'Improved communication effectiveness',
                'Cost savings and ROI achievement',
                'Increased resident satisfaction scores',
                'Faster resolution of issues and requests',
                'Better financial reporting and transparency',
                'Enhanced security and compliance',
                'Successful integration with existing systems',
              ].map((metric) => (
                <div key={metric} className="flex items-start space-x-2">
                  <Checkbox
                    id={`metric-${metric}`}
                    checked={data.successMetrics?.includes(metric) || false}
                    onCheckedChange={(checked) =>
                      handleArrayUpdate('successMetrics', metric, !!checked)
                    }
                  />
                  <Label htmlFor={`metric-${metric}`} className="text-sm leading-relaxed">
                    {metric}
                  </Label>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <Label htmlFor="customSuccessMetrics">Additional success metrics or goals</Label>
              <Textarea
                id="customSuccessMetrics"
                value={data.customSuccessMetrics || ''}
                onChange={(e) => updateFormData('customSuccessMetrics', e.target.value)}
                placeholder="e.g., Specific KPIs, unique goals, custom benchmarks..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Risk Mitigation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Risk Mitigation</CardTitle>
          <CardDescription>
            What are your main concerns about enterprise implementation?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              'User resistance to change',
              'Technical complexity and integration challenges',
              'Data migration and security concerns',
              'Timeline delays and project overruns',
              'Budget overages and unexpected costs',
              'Disruption to current operations',
              'Vendor support and reliability',
              'Staff training and learning curve',
              'Board approval and stakeholder buy-in',
              'Customization and configuration complexity',
            ].map((concern) => (
              <div key={concern} className="flex items-start space-x-2">
                <Checkbox
                  id={`concern-${concern}`}
                  checked={data.implementationConcerns?.includes(concern) || false}
                  onCheckedChange={(checked) =>
                    handleArrayUpdate('implementationConcerns', concern, !!checked)
                  }
                />
                <Label htmlFor={`concern-${concern}`} className="text-sm leading-relaxed">
                  {concern}
                </Label>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <Label htmlFor="riskMitigation">How can we help address these concerns?</Label>
            <Textarea
              id="riskMitigation"
              value={data.riskMitigation || ''}
              onChange={(e) => updateFormData('riskMitigation', e.target.value)}
              placeholder="e.g., Need detailed project plan, want proof of concept, require references from similar implementations..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Help Text */}
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
        <p className="text-sm text-teal-700">
          <strong>
            Implementation readiness assessment helps us plan your enterprise rollout:
          </strong>
        </p>
        <ul className="text-sm text-teal-700 mt-2 space-y-1">
          <li>• Design implementation strategy matching your capacity and approach</li>
          <li>• Plan training programs tailored to your team's needs and preferences</li>
          <li>• Structure support model to provide the right level of ongoing assistance</li>
          <li>• Identify and mitigate risks early in the implementation process</li>
          <li>• Set realistic expectations and success metrics for enterprise deployment</li>
        </ul>
      </div>
    </div>
  );
};
